import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShareNetwork, DeviceMobile, Money, Copy, WhatsappLogo, MagnifyingGlass, Trophy, Crown, Medal, CaretUp } from "@phosphor-icons/react";
import { referralSignup, getReferralStats, getReferralLeaderboard } from "@/lib/api";
import { formatGHS } from "@/lib/format";

const NETWORKS = ["MTN", "Telecel", "AirtelTigo"];

export default function ReferralPage() {
  return (
    <div data-testid="referral-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <div className="border-b border-white/10 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FFC107] mb-4">// Refer &amp; Earn</p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight leading-[0.95]">
            Earn 5% on<br />every sale
          </h1>
          <p className="mt-6 max-w-xl text-zinc-400 text-base md:text-lg leading-relaxed">
            Share your personal link. Earn 5% of every order placed through it —
            tracked automatically on your code and paid out in cash every week.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          <Step icon={ShareNetwork} n="01" title="Get your link" text="Sign up with your name and phone number. You get a unique referral link instantly." />
          <Step icon={DeviceMobile} n="02" title="Share it" text="Send it on WhatsApp, post it, drop it in group chats — anywhere in Ghana." />
          <Step icon={Money} n="03" title="Get paid weekly" text="Every sale through your link earns you 5%. We track it on your code and pay you in cash every week." />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <SignupCard />
          <EarningsCard />
        </div>

        <Leaderboard />
      </div>
    </div>
  );
}

const Step = ({ icon: Icon, n, title, text }) => (
  <div className="border border-white/10 bg-[#141414] p-6">
    <div className="flex items-center justify-between mb-4">
      <Icon size={28} weight="duotone" className="text-[#FFC107]" />
      <span className="font-mono-accent text-xs text-zinc-600">{n}</span>
    </div>
    <h3 className="font-display font-black uppercase mb-2">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{text}</p>
  </div>
);

const SignupCard = () => {
  const [form, setForm] = useState({ name: "", phone: "", network: "MTN", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const shareUrl = result ? `${window.location.origin}/?ref=${result.code}` : "";
  const waText = encodeURIComponent(
    `Protect your bike, car or shop with ObraSafe alarms & GPS trackers. Shop here: ${shareUrl}`
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await referralSignup({ ...form, email: form.email || null });
      setResult(data);
      toast.success(data.existing ? "Welcome back! Here's your existing link." : "You're in! Share your link and start earning.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="border border-[#FFC107]/40 bg-[#141414] p-8" data-testid="referral-result">
        <h2 className="font-display text-2xl font-black uppercase mb-2">Your referral link</h2>
        <p className="text-sm text-zinc-400 mb-6">Code: <span className="font-mono-accent text-[#FFC107] text-lg" data-testid="referral-code">{result.code}</span></p>
        <div className="flex items-stretch gap-2 mb-4">
          <input readOnly value={shareUrl} data-testid="referral-link" className="flex-1 h-12 px-4 bg-[#0a0a0a] border border-white/15 text-sm font-mono-accent text-zinc-300 outline-none" />
          <button
            data-testid="copy-referral-link"
            onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }}
            className="px-4 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107]"
            aria-label="Copy link"
          >
            <Copy size={18} weight="bold" />
          </button>
        </div>
        <a
          href={`https://wa.me/?text=${waText}`}
          target="_blank"
          rel="noreferrer"
          data-testid="share-whatsapp"
          className="w-full h-12 bg-[#25D366] hover:bg-[#1fb857] text-black font-black uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-colors"
        >
          <WhatsappLogo size={20} weight="fill" /> Share on WhatsApp
        </a>
        <p className="text-[11px] text-zinc-500 mt-4">
          5% of every Paystack order through your link is tracked on code {result.code} and paid out in cash weekly. Check your earnings anytime below.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-white/10 bg-[#141414] p-8" data-testid="referral-signup-form">
      <h2 className="font-display text-2xl font-black uppercase mb-6">Join the program</h2>
      <div className="space-y-4">
        <input data-testid="ref-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
        <input data-testid="ref-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number (e.g. 0244 000 000)" className="w-full h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
        <select data-testid="ref-network" value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })} className="w-full h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm">
          {NETWORKS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <input data-testid="ref-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="w-full h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
        <button data-testid="ref-signup-btn" disabled={submitting} type="submit" className="w-full h-12 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm disabled:opacity-50">
          {submitting ? "Creating your link…" : "Get my referral link"}
        </button>
      </div>
    </form>
  );
};

const EarningsCard = () => {
  const [code, setCode] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setStats(await getReferralStats(code.trim()));
    } catch {
      setStats(null);
      toast.error("Code not found. Check and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#141414] p-8" data-testid="earnings-checker">
      <h2 className="font-display text-2xl font-black uppercase mb-6">Check your earnings</h2>
      <form onSubmit={check} className="flex items-stretch gap-2 mb-6">
        <input data-testid="earnings-code-input" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Your referral code (e.g. AB3K9X)" className="flex-1 h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none text-sm font-mono-accent uppercase" />
        <button data-testid="earnings-check-btn" disabled={loading} className="px-5 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black disabled:opacity-50" aria-label="Check earnings">
          <MagnifyingGlass size={18} weight="bold" />
        </button>
      </form>
      {stats && (
        <div data-testid="earnings-stats">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-400">
              Hi <span className="text-white font-bold">{stats.name}</span>
              {stats.rank && (
                <span className="ml-2 text-[#FFC107] font-bold" data-testid="earnings-rank">#{stats.rank} on the leaderboard</span>
              )}
            </p>
            <TierChip tier={stats.tier} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Referrals" value={stats.referral_count} />
            <Stat label="Total earned" value={formatGHS(stats.total_earned)} />
            <Stat label="Paid out" value={formatGHS(stats.total_paid)} accent />
            <Stat label="Awaiting weekly payout" value={formatGHS(stats.pending_amount)} />
          </div>
          {stats.next_tier && (
            <div className="mt-4" data-testid="tier-progress">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-widest mb-2">
                <span className="text-zinc-500">Next rank: <span className="text-white font-bold">{stats.next_tier.name}</span></span>
                <span className="text-[#FFC107] flex items-center gap-1 font-bold">
                  <CaretUp size={12} weight="bold" /> {stats.next_tier.referrals_needed} more sale{stats.next_tier.referrals_needed > 1 ? "s" : ""}
                </span>
              </div>
              <div className="h-2 bg-[#0a0a0a] border border-white/10 overflow-hidden">
                <div
                  className="h-full bg-[#FFC107] transition-all duration-700"
                  style={{ width: `${Math.min(100, (stats.referral_count / stats.next_tier.threshold) * 100)}%` }}
                />
              </div>
            </div>
          )}
          <a
            href={`https://wa.me/?text=${encodeURIComponent(
              (stats.rank
                ? `I'm ranked #${stats.rank} (${stats.tier}) on ObraSafe's Hall of Fame 🏆 `
                : "I'm on ObraSafe's referral program. ") +
                `Protect your bike, car or shop with alarms & GPS trackers — buy through my link: ${window.location.origin}/?ref=${stats.code}`
            )}`}
            target="_blank"
            rel="noreferrer"
            data-testid="share-rank-whatsapp"
            className="mt-4 w-full h-11 bg-[#25D366] hover:bg-[#1fb857] text-black font-black uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
          >
            <WhatsappLogo size={18} weight="fill" /> {stats.rank ? `Brag about rank #${stats.rank} on WhatsApp` : "Share my link on WhatsApp"}
          </a>
        </div>
      )}
      {!stats && (
        <p className="text-sm text-zinc-500">Enter the code you received when you signed up to see your referrals, earnings, and payouts.</p>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="border border-white/10 bg-[#0a0a0a] p-4">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    <div className={`font-display text-xl font-black ${accent ? "text-[#FFC107]" : ""}`}>{value}</div>
  </div>
);

const TIER_STYLES = {
  Legend: "border-[#D32F2F] text-[#D32F2F]",
  Gold: "border-[#FFC107] text-[#FFC107]",
  Silver: "border-zinc-300 text-zinc-300",
  Bronze: "border-[#cd7f32] text-[#cd7f32]",
  Rookie: "border-zinc-600 text-zinc-500",
};

const TierChip = ({ tier }) => (
  <span
    data-testid={`tier-chip-${tier?.toLowerCase()}`}
    className={`inline-flex items-center gap-1 border px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-black ${TIER_STYLES[tier] || TIER_STYLES.Rookie}`}
  >
    <Medal size={12} weight="fill" /> {tier}
  </span>
);

const MEDAL_COLORS = ["#FFC107", "#c0c0c0", "#cd7f32"];

const PodiumSpot = ({ row, place }) => {
  const heights = { 1: "h-28 md:h-36", 2: "h-20 md:h-24", 3: "h-14 md:h-16" };
  return (
    <div className="flex-1 flex flex-col items-center justify-end" data-testid={`podium-${place}`}>
      {place === 1 && <Crown size={30} weight="fill" className="text-[#FFC107] mb-1 animate-bounce" />}
      <div
        className="w-12 h-12 rounded-full border-2 flex items-center justify-center font-display font-black text-lg mb-2 bg-[#0a0a0a]"
        style={{ borderColor: MEDAL_COLORS[place - 1], color: MEDAL_COLORS[place - 1] }}
      >
        {row.name.charAt(0)}
      </div>
      <p className="font-bold text-sm mb-0.5">{row.name}</p>
      <p className="font-mono-accent text-[#FFC107] text-sm mb-1">{formatGHS(row.total_earned)}</p>
      <TierChip tier={row.tier} />
      <div
        className={`w-full mt-3 ${heights[place]} border border-white/10 flex items-start justify-center pt-2`}
        style={{ background: `linear-gradient(180deg, ${MEDAL_COLORS[place - 1]}22, transparent)` }}
      >
        <span className="font-display font-black text-2xl" style={{ color: MEDAL_COLORS[place - 1] }}>{place}</span>
      </div>
    </div>
  );
};

const Leaderboard = () => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    getReferralLeaderboard().then(setRows).catch(() => {});
  }, []);

  const podium = rows.slice(0, 3);
  const podiumOrder = [podium[1], podium[0], podium[2]].filter(Boolean);
  const rest = rows.slice(3);

  return (
    <div className="mt-16" data-testid="referral-leaderboard">
      <div className="flex items-center gap-3 mb-6">
        <Trophy size={28} weight="duotone" className="text-[#FFC107]" />
        <h2 className="font-display text-2xl font-black uppercase">Hall of fame</h2>
      </div>
      {rows.length === 0 ? (
        <div className="border border-white/10 bg-[#141414] p-8 text-center text-zinc-500 text-sm" data-testid="leaderboard-empty">
          The Hall of Fame is wide open. Be the first name on the podium — grab your link above and start sharing.
        </div>
      ) : (
        <div className="border border-white/10 bg-[#141414]">
          <div className="flex items-end gap-3 md:gap-8 px-6 pt-8 pb-0 max-w-2xl mx-auto">
            {podiumOrder.map((row) => (
              <PodiumSpot key={row.rank} row={row} place={row.rank} />
            ))}
          </div>
          {rest.length > 0 && (
            <div className="border-t border-white/10">
              {rest.map((r) => (
                <div
                  key={r.rank}
                  data-testid={`leaderboard-row-${r.rank}`}
                  className="flex items-center gap-4 px-6 py-3 border-b border-white/5 hover:bg-white/5 text-sm"
                >
                  <span className="font-display font-black text-zinc-600 w-8">{r.rank}</span>
                  <span className="font-bold flex-1">{r.name}</span>
                  <TierChip tier={r.tier} />
                  <span className="text-zinc-500 w-20 text-right">{r.referral_count} sale{r.referral_count !== 1 ? "s" : ""}</span>
                  <span className="font-mono-accent text-[#FFC107] w-28 text-right">{formatGHS(r.total_earned)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-zinc-600 text-center py-4 uppercase tracking-widest">
            Top 100 · Ranks: Bronze 1+ · Silver 5+ · Gold 10+ · Legend 25+ sales
          </p>
        </div>
      )}
    </div>
  );
};
